import { action, computed, makeObservable, reaction } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { CLOSED_STATUS_TYPES, isClosedStatus } from '@/tracker/entities/status';
import { DEFAULT_SORT, IssueSort } from '@/tracker/features/issueList';
import { TrackerStore } from '@/tracker/model';
import { TaskBasic, TaskFilters } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

const SEARCH_DELAY_MS = 300;

export class MyIssuesStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _userId: string | null;
  private readonly _issues = new ValueModel<TaskBasic[]>([]);
  /** Закрытых по тем же условиям — считает сервер, сами закрытые грузим, только когда их показывают */
  private readonly _closedCount = new ValueModel(0);
  private readonly _showClosed = new ValueModel(false);
  private readonly _query = new ValueModel('');
  private readonly _sort = new ValueModel<IssueSort>(DEFAULT_SORT);
  private readonly _loadingStage = new LoadingStageModel();
  /** Ответ на устаревший поиск или сортировку не должен перетереть свежий */
  private _request = 0;

  constructor({
    alertsStore,
    trackerStore,
    userId,
  }: {
    alertsStore: AlertsStore;
    trackerStore: TrackerStore;
    userId: string | null;
  }) {
    super();
    this._alertsStore = alertsStore;
    this._userId = userId;

    makeObservable(this, {
      issues: computed,
      closedCount: computed,
      showClosed: computed,
      query: computed,
      sort: computed,
      totalCount: computed,
      isLoading: computed,
      isError: computed,
      load: action.bound,
      setShowClosed: action.bound,
      setQuery: action.bound,
      setSort: action.bound,
    });

    this.addReactions([
      reaction(
        () => this._query.value,
        () => this.load(),
        { delay: SEARCH_DELAY_MS },
      ),
      reaction(
        () => [this._sort.value, this._showClosed.value],
        () => this.load(),
      ),
    ]);

    // Задачу поменяли в карточке — обновляем строку без перезагрузки списка
    this.addReaction(
      reaction(
        () => trackerStore.updatedIssue,
        (updated) => {
          if (!updated) return;

          this._issues.change(
            this._issues.value.map((issue) => (issue.key === updated.key ? updated : issue)),
          );
        },
      ),
    );
  }

  /** В порядке сервера; закрытую в карточке задачу прячем сразу, не дожидаясь перезагрузки */
  get issues(): TaskBasic[] {
    if (this._showClosed.value) return this._issues.value;

    return this._issues.value.filter((issue) => !isClosedStatus(issue.status));
  }

  get closedCount(): number {
    return this._closedCount.value;
  }

  /** Найдено всего, с закрытыми */
  get totalCount(): number {
    return this._showClosed.value
      ? this._issues.value.length
      : this._issues.value.length + this._closedCount.value;
  }

  get query() {
    return this._query.value;
  }

  get sort() {
    return this._sort.value;
  }

  get showClosed(): boolean {
    return this._showClosed.value;
  }

  get isLoading(): boolean {
    return this._loadingStage.isLoading;
  }

  get isError(): boolean {
    return this._loadingStage.isError;
  }

  setShowClosed(value: boolean) {
    this._showClosed.change(value);
  }

  setQuery(query: string) {
    this._query.change(query);
  }

  setSort(sort: IssueSort) {
    this._sort.change(sort);
  }

  async load() {
    // Без пользователя нельзя: пустой assignee в API значит «все задачи»
    if (!this._userId) return;

    const request = ++this._request;

    try {
      this._loadingStage.loading();

      const query = this._query.value.trim() || undefined;
      const mine: TaskFilters = { assignee: { eq: this._userId } };
      const [issues, closed] = await Promise.all([
        trackerApi.searchAllTasks({
          query,
          filters: this._showClosed.value
            ? mine
            : { ...mine, statusType: { notIn: CLOSED_STATUS_TYPES } },
          orderBy: [this._sort.value],
        }),
        // Только число: pageSize 1 и totalSize
        trackerApi.searchTasks({
          query,
          filters: { ...mine, statusType: { in: CLOSED_STATUS_TYPES } },
          pageSize: 1,
        }),
      ]);

      if (request !== this._request) return;

      this._issues.change(issues);
      this._closedCount.change(closed.totalSize);
      this._loadingStage.success();
    } catch (error) {
      if (request !== this._request) return;

      this._loadingStage.error();

      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить задачи', error });
    }
  }
}
